/**
 * Token Refresh Service
 * 
 * Handles automatic JWT token refresh before expiration.
 * - Decodes JWT tokens to determine expiration time
 * - Schedules automatic refresh at 95% of token lifetime (23h for 24h tokens)
 * - Handles refresh failures with retry logic
 * - Integrates with the auth store for logout on refresh failure
 */

interface JWTPayload {
  userId: string;
  email: string;
  role: string;
  iat: number; // Issued at timestamp (seconds)
  exp: number; // Expiration timestamp (seconds)
}

interface TokenMetadata {
  issuedAt: number; // Milliseconds
  expiresAt: number; // Milliseconds
  refreshScheduledAt: number; // Milliseconds - when refresh should occur
}

class TokenRefreshService {
  private refreshTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private refreshCallback: (() => Promise<void>) | null = null;
  private logoutCallback: (() => void) | null = null;
  
  /**
   * Decode a JWT token to extract expiration information
   */
  private decodeToken(token: string): JWTPayload | null {
    try {
      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('TokenRefreshService: Invalid JWT format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      
      return decoded as JWTPayload;
    } catch (error) {
      console.error('TokenRefreshService: Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Calculate token metadata from JWT
   */
  getTokenMetadata(token: string): TokenMetadata | null {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp || !decoded.iat) {
      return null;
    }

    const issuedAt = decoded.iat * 1000; // Convert to milliseconds
    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const tokenLifetime = expiresAt - issuedAt;
    
    // Schedule refresh at 95% of token lifetime (23h for 24h tokens)
    // This gives 1 hour buffer before actual expiration
    const refreshScheduledAt = issuedAt + (tokenLifetime * 0.95);

    return {
      issuedAt,
      expiresAt,
      refreshScheduledAt,
    };
  }

  /**
   * Get time remaining until token expires (in milliseconds)
   */
  getTimeUntilExpiration(token: string): number | null {
    const metadata = this.getTokenMetadata(token);
    if (!metadata) return null;

    const now = Date.now();
    return Math.max(0, metadata.expiresAt - now);
  }

  /**
   * Get time remaining until scheduled refresh (in milliseconds)
   */
  getTimeUntilRefresh(token: string): number | null {
    const metadata = this.getTokenMetadata(token);
    if (!metadata) return null;

    const now = Date.now();
    return Math.max(0, metadata.refreshScheduledAt - now);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const metadata = this.getTokenMetadata(token);
    if (!metadata) return true;

    return Date.now() >= metadata.expiresAt;
  }

  /**
   * Check if token should be refreshed now
   */
  shouldRefreshToken(token: string): boolean {
    const metadata = this.getTokenMetadata(token);
    if (!metadata) return false;

    return Date.now() >= metadata.refreshScheduledAt;
  }

  /**
   * Schedule automatic token refresh
   */
  scheduleRefresh(
    token: string,
    refreshCallback: () => Promise<void>,
    logoutCallback: () => void
  ): void {
    // Clear any existing timer
    this.stopRefresh();

    this.refreshCallback = refreshCallback;
    this.logoutCallback = logoutCallback;

    const timeUntilRefresh = this.getTimeUntilRefresh(token);
    
    if (timeUntilRefresh === null) {
      console.error('TokenRefreshService: Cannot schedule refresh - invalid token');
      return;
    }

    if (timeUntilRefresh <= 0) {
      // Token should be refreshed immediately
      console.log('TokenRefreshService: Token needs immediate refresh');
      this.performRefresh();
      return;
    }

    const metadata = this.getTokenMetadata(token);
    if (metadata) {
      const refreshDate = new Date(metadata.refreshScheduledAt);
      const expiryDate = new Date(metadata.expiresAt);
      console.log(`TokenRefreshService: Scheduled refresh at ${refreshDate.toLocaleString()} (expires at ${expiryDate.toLocaleString()})`);
    }

    // Schedule the refresh
    this.refreshTimer = setTimeout(() => {
      this.performRefresh();
    }, timeUntilRefresh);
  }

  /**
   * Perform the actual token refresh
   */
  private async performRefresh(): Promise<void> {
    if (this.isRefreshing) {
      console.log('TokenRefreshService: Refresh already in progress');
      return;
    }

    if (!this.refreshCallback) {
      console.error('TokenRefreshService: No refresh callback set');
      return;
    }

    this.isRefreshing = true;
    console.log('TokenRefreshService: Starting token refresh...');

    try {
      await this.refreshCallback();
      console.log('TokenRefreshService: Token refreshed successfully');
      this.isRefreshing = false;
    } catch (error) {
      console.error('TokenRefreshService: Token refresh failed:', error);
      this.isRefreshing = false;
      
      // On refresh failure, log the user out
      if (this.logoutCallback) {
        console.log('TokenRefreshService: Logging out due to refresh failure');
        this.logoutCallback();
      }
    }
  }

  /**
   * Manually trigger a token refresh (for testing or immediate refresh needs)
   */
  async refreshNow(): Promise<void> {
    await this.performRefresh();
  }

  /**
   * Stop the automatic refresh timer
   */
  stopRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      console.log('TokenRefreshService: Refresh timer stopped');
    }
  }

  /**
   * Get token information for debugging
   */
  getTokenInfo(token: string): {
    isExpired: boolean;
    shouldRefresh: boolean;
    timeUntilExpiration: string;
    timeUntilRefresh: string;
  } | null {
    const metadata = this.getTokenMetadata(token);
    if (!metadata) return null;

    const now = Date.now();
    const timeUntilExpiration = metadata.expiresAt - now;
    const timeUntilRefresh = metadata.refreshScheduledAt - now;

    return {
      isExpired: now >= metadata.expiresAt,
      shouldRefresh: now >= metadata.refreshScheduledAt,
      timeUntilExpiration: this.formatDuration(Math.max(0, timeUntilExpiration)),
      timeUntilRefresh: this.formatDuration(Math.max(0, timeUntilRefresh)),
    };
  }

  /**
   * Format milliseconds into human-readable duration
   */
  private formatDuration(ms: number): string {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
